import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import styled from 'styled-components';
import DOMPurify from 'dompurify';

const InfoButtonStyled = styled.button`
  background: rgba(0, 212, 255, 0.1);
  border: 1px solid rgba(0, 212, 255, 0.3);
  border-radius: 50%;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  color: #00d4ff;
  font-family: 'Courier New', monospace;
  font-size: 14px;
  font-weight: bold;

  &:hover {
    background: rgba(0, 212, 255, 0.2);
    border-color: #00d4ff;
    transform: scale(1.1);
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.85);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1100;
  padding: 20px;
`;

const ModalContent = styled.div`
  background: #0d1a26;
  border: 1px solid rgba(0, 212, 255, 0.3);
  border-radius: 16px;
  max-width: 650px;
  width: 100%;
  max-height: 85vh;
  overflow-y: auto;
`;

const ModalHeader = styled.div`
  padding: 20px 24px;
  border-bottom: 1px solid rgba(0, 212, 255, 0.2);
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: sticky;
  top: 0;
  background: #0d1a26;
  z-index: 1;
`;

const ModalTitle = styled.h3`
  color: #00d4ff;
  margin: 0;
  font-size: 1.2rem;
`;

const CloseButton = styled.button`
  background: transparent;
  border: none;
  color: #888;
  font-size: 1.5rem;
  cursor: pointer;
  padding: 0;
  line-height: 1;

  &:hover {
    color: #fff;
  }
`;

const ModalBody = styled.div`
  padding: 24px;
`;

const InfoSectionStyled = styled.div`
  margin-bottom: 24px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const InfoTitle = styled.h4`
  font-size: 0.95rem;
  color: #00d4ff;
  text-transform: uppercase;
  letter-spacing: 1px;
  margin: 0 0 12px 0;
  padding-bottom: 8px;
  border-bottom: 1px solid rgba(0, 212, 255, 0.2);
`;

const InfoText = styled.p`
  font-size: 0.9rem;
  line-height: 1.7;
  color: #b0b0b0;
  margin: 0 0 12px 0;

  &:last-child {
    margin-bottom: 0;
  }
`;

const FieldList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const FieldItem = styled.li`
  padding: 12px 14px;
  margin-bottom: 8px;
  background: rgba(0, 30, 60, 0.5);
  border: 1px solid rgba(0, 212, 255, 0.15);
  border-radius: 8px;
  font-size: 0.85rem;

  &:last-child {
    margin-bottom: 0;
  }
`;

const FieldName = styled.span`
  color: #00d4ff;
  font-weight: 600;
  display: block;
  margin-bottom: 4px;
`;

const FieldDescription = styled.span`
  color: #999;
  line-height: 1.5;
`;

export interface InfoField {
  name: string;
  description: string;
}

export interface InfoSectionData {
  title: string;
  description?: string;
  fields?: InfoField[];
}

interface InfoButtonProps {
  title: string;
  sections: InfoSectionData[];
}

const InfoButton: React.FC<InfoButtonProps> = ({ title, sections }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <>
      <InfoButtonStyled onClick={() => setIsOpen(true)} title="Show help">
        ?
      </InfoButtonStyled>
      {isOpen && ReactDOM.createPortal(
        <ModalOverlay onClick={() => setIsOpen(false)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>{title}</ModalTitle>
              <CloseButton onClick={() => setIsOpen(false)}>&times;</CloseButton>
            </ModalHeader>
            <ModalBody>
              {sections.map((section, index) => (
                <InfoSectionStyled key={index}>
                  <InfoTitle>{section.title}</InfoTitle>
                  {section.description && (
                    <InfoText dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(section.description || '') }} />
                  )}
                  {section.fields && section.fields.length > 0 && (
                    <FieldList>
                      {section.fields.map((field, fieldIndex) => (
                        <FieldItem key={fieldIndex}>
                          <FieldName>{field.name}</FieldName>
                          <FieldDescription dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(field.description || '') }} />
                        </FieldItem>
                      ))}
                    </FieldList>
                  )}
                </InfoSectionStyled>
              ))}
            </ModalBody>
          </ModalContent>
        </ModalOverlay>,
        document.body
      )}
    </>
  );
};

export default InfoButton;
